import { useState } from "react";
import type { PackDefinition } from "types/packs/pack-definition";
import { PackVariant } from "types/packs/pack-definition";
import { packDisplayName, packImageUrl } from "utils/packs/pack-image-url";

const VARIANT_BADGE: Partial<
  Record<PackVariant, { label: string; bg: string; text: string; glow: string }>
> = {
  [PackVariant.SHINY_BOOST]: {
    label: "✦ SHINY",
    bg: "linear-gradient(135deg, #ffe066 0%, #ffb300 40%, #fff5a0 60%, #ffb300 100%)",
    text: "#5a3a00",
    glow: "0 0 6px 2px #ffe06699, 0 1px 3px rgba(0,0,0,0.6)",
  },
  [PackVariant.TALENT_BOOST]: {
    label: "★ TALENT",
    bg: "linear-gradient(135deg, #c084fc 0%, #7c3aed 40%, #e0aaff 60%, #7c3aed 100%)",
    text: "#fff",
    glow: "0 0 6px 2px #a855f799, 0 1px 3px rgba(0,0,0,0.6)",
  },
};

/**
 * Booster-pack visual: renders the generated pixel-art shell image only.
 * Falls back to the pack name on a neutral card if the image is missing.
 * Variant badges (Shiny/Talent Boost) are overlaid as promo stickers.
 */
export const PackCardVisual = ({
  definition,
  className = "",
  width = 170,
}: {
  definition: PackDefinition;
  className?: string;
  /** Render width in px. Height follows the 5:7 pack aspect. Defaults to 170. */
  width?: number;
}) => {
  const [imgFailed, setImgFailed] = useState(false);
  const imgSrc = packImageUrl(definition.key);
  const showImage = Boolean(imgSrc) && !imgFailed;
  const badge = definition.variant ? VARIANT_BADGE[definition.variant] : undefined;

  return (
    <div
      className={`relative ${className}`}
      style={{ width, aspectRatio: "5 / 7" }}
    >
      {showImage ? (
        <img
          src={imgSrc!}
          alt={definition.name}
          className="w-full h-full object-contain"
          style={{ imageRendering: "pixelated" }}
          onError={() => setImgFailed(true)}
        />
      ) : (
        <div className="flex justify-center items-center bg-black/40 border border-white/15 rounded-xl w-full h-full">
          <span
            className="px-3 font-black text-[15px] text-white text-center uppercase tracking-[0.08em] leading-tight"
            style={{ textShadow: "0 2px 4px rgba(0,0,0,0.85)" }}
          >
            {packDisplayName(definition.name)}
          </span>
        </div>
      )}

      {badge && (
        <span
          className="absolute bottom-[8%] right-[4%] z-10 rotate-[-8deg] select-none rounded-[4px] px-1.5 py-0.5 text-[8px] font-black uppercase tracking-[0.12em] leading-none"
          style={{
            background: badge.bg,
            color: badge.text,
            boxShadow: badge.glow,
            border: "1px solid rgba(255,255,255,0.35)",
          }}
        >
          {badge.label}
        </span>
      )}
    </div>
  );
};
